import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { wholesaleOrders, wholesaleProducts, wholesalePickupLocations, wholesaleShipping } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ShoppingCart, CreditCard, FileText, AlertCircle, CheckCircle, Truck, MapPin } from 'lucide-react';

// Minimum order threshold (mirrors backend default — backend is authoritative)
const MIN_BOTTLES = 12;

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const fmt = (n) =>
  '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CARD_STYLE = {
  style: {
    base: { fontSize: '16px', color: '#212529', '::placeholder': { color: '#6c757d' } },
    invalid: { color: '#dc3545' },
  },
};

function CheckoutForm() {
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  const stripe   = useStripe();
  const elements = useElements();

  const [summary,       setSummary]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState('');
  const [chosenMethod,  setChosenMethod]  = useState('invoice');
  const [productNames,  setProductNames]  = useState({});

  // Fulfillment
  const [fulfillmentType,   setFulfillmentType]   = useState('ship');
  const [pickupLocations,   setPickupLocations]   = useState([]);
  const [pickupLocationId,  setPickupLocationId]  = useState('');

  // Shipping cost (rules engine)
  const [shippingInfo, setShippingInfo] = useState({
    shipping_cost:             0,
    shipping_rule_id:          null,
    shipping_rule_description: '',
    total_cases:               0,
    cases_until_free:          null,
  });
  const shippingDebounce = useRef(null);

  // ── Fetch product names ──────────────────────────────────────────────────────
  useEffect(() => {
    wholesaleProducts.list().then((res) => {
      const data = res?.data?.data || res?.data || [];
      const map  = {};
      (Array.isArray(data) ? data : []).forEach((p) => {
        map[p.productID || p.id] = p.name || p.productName;
      });
      setProductNames(map);
    }).catch(() => {});
  }, []);

  // ── Fetch pickup locations ───────────────────────────────────────────────────
  useEffect(() => {
    wholesalePickupLocations.list()
      .then(res => {
        const locs = res?.data?.data || [];
        setPickupLocations(locs);
        if (locs.length) setPickupLocationId(String(locs[0].id));
      })
      .catch(() => {});
  }, []);

  // ── Calculate order on mount ─────────────────────────────────────────────────
  useEffect(() => {
    if (!cart || Object.keys(cart).length === 0) { setLoading(false); return; }
    setLoading(true);
    wholesaleOrders.calculate({ cart })
      .then((res) => {
        const data = res?.data?.data || res?.data;
        setSummary(data);
        if (data?.paymentType && data.paymentType !== 'both') {
          setChosenMethod(data.paymentType);
        } else {
          setChosenMethod('invoice');
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'Failed to calculate order. Please try again.');
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Real-time shipping recalculation ────────────────────────────────────────
  // Debounce 400ms so rapid cart changes don't flood the API.
  useEffect(() => {
    if (!cart || Object.keys(cart).length === 0) return;

    if (shippingDebounce.current) clearTimeout(shippingDebounce.current);
    shippingDebounce.current = setTimeout(() => {
      wholesaleShipping.calculate(cart)
        .then(res => {
          const d = res?.data?.data;
          if (d) setShippingInfo(d);
        })
        .catch(() => {});
    }, 400);

    return () => { if (shippingDebounce.current) clearTimeout(shippingDebounce.current); };
  }, [cart]);

  // ── Derived values ───────────────────────────────────────────────────────────
  const isCartEmpty   = !cart || Object.keys(cart).length === 0;
  const totalBottles  = Object.values(cart || {}).reduce((s, q) => s + (parseInt(q) || 0), 0);
  const belowMinimum  = totalBottles < MIN_BOTTLES;

  const items            = summary?.items           || [];
  const subtotal         = summary?.subtotal        || 0;
  const shippingAddress  = summary?.shippingAddress;
  const paymentType      = summary?.paymentType     || 'invoice';
  const netDays          = summary?.netDays;
  const dueDate          = summary?.dueDate;
  const availableCredit  = summary?.availableCredit;

  const effectiveMethod = paymentType === 'both' ? chosenMethod : paymentType;

  const insufficientCredit =
    effectiveMethod === 'invoice' &&
    availableCredit !== null && availableCredit !== undefined &&
    parseFloat(availableCredit) < parseFloat(subtotal);

  const shippingCost = fulfillmentType === 'ship' ? (shippingInfo.shipping_cost || 0) : 0;
  const orderTotal   = parseFloat(subtotal) + parseFloat(shippingCost);
  const itemCount    = items.reduce((acc, item) => acc + (item.quantity || 0), 0);

  const isSubmitDisabled =
    submitting || insufficientCredit || belowMinimum ||
    (fulfillmentType === 'pickup' && !pickupLocationId) ||
    (effectiveMethod === 'credit_card' && !stripe);

  // ── Form submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    if (fulfillmentType === 'pickup' && !pickupLocationId) {
      setError('Please select a pickup location.');
      setSubmitting(false);
      return;
    }

    const fulfillmentPayload = {
      fulfillmentType,
      ...(fulfillmentType === 'pickup' && { pickup_location_id: parseInt(pickupLocationId) }),
      shipping_cost:             shippingCost,
      shipping_rule_id:          fulfillmentType === 'ship' ? (shippingInfo.shipping_rule_id || null) : null,
      shipping_rule_description: fulfillmentType === 'ship' ? (shippingInfo.shipping_rule_description || null) : null,
    };

    try {
      const effectiveType = summary?.paymentType === 'both' ? chosenMethod : summary?.paymentType || chosenMethod;

      if (effectiveType === 'invoice') {
        const res = await wholesaleOrders.create({ cart, chosenMethod: 'invoice', ...fulfillmentPayload });
        const data = res?.data?.data || res?.data;
        clearCart();
        navigate(`/wholesale/orders/${data.orderId}`);
      } else {
        if (!stripe || !elements) {
          setError('Stripe has not loaded yet. Please wait a moment and try again.');
          setSubmitting(false);
          return;
        }
        const cardElement = elements.getElement(CardElement);
        const { error: pmError, paymentMethod: pm } = await stripe.createPaymentMethod({ type: 'card', card: cardElement });
        if (pmError) {
          setError(pmError.message || 'Card error. Please check your details.');
          setSubmitting(false);
          return;
        }
        const res = await wholesaleOrders.create({ cart, paymentMethodId: pm.id, chosenMethod: 'credit_card', ...fulfillmentPayload });
        const data = res?.data?.data || res?.data;
        if (data?.clientSecret) {
          const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
          if (confirmError) {
            setError(confirmError.message || 'Payment confirmation failed.');
            setSubmitting(false);
            return;
          }
        }
        clearCart();
        navigate(`/wholesale/orders/${data.orderId}`);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  // ── Empty / loading states ───────────────────────────────────────────────────
  if (!loading && isCartEmpty) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm text-center p-5">
              <div className="mb-4"><ShoppingCart size={64} className="text-muted" /></div>
              <h4 className="mb-2">Your cart is empty</h4>
              <p className="text-muted mb-4">Add some products before checking out.</p>
              <Link to="/wholesale/catalog" className="btn btn-primary px-4">Browse Catalog</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Calculating your order…</p>
      </div>
    );
  }

  // ── Sub-components ───────────────────────────────────────────────────────────
  const InvoiceSection = () => (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <FileText size={18} className="text-secondary" />
        <span className="fw-semibold">Invoice Terms</span>
      </div>
      {netDays != null && dueDate && (
        <p className="mb-2 text-muted">
          Terms: <strong>Net {netDays} days</strong> — Due{' '}
          <strong>{new Date(dueDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
        </p>
      )}
      {availableCredit !== null && availableCredit !== undefined && (
        <p className={`mb-2 fw-semibold ${parseFloat(availableCredit) >= parseFloat(subtotal) ? 'text-success' : 'text-danger'}`}>
          Available Credit: {fmt(availableCredit)}
        </p>
      )}
      {insufficientCredit && (
        <div className="alert alert-danger d-flex align-items-start gap-2 py-2 px-3 mt-2" role="alert">
          <AlertCircle size={18} className="flex-shrink-0 mt-1" />
          <div>
            <strong>Insufficient credit.</strong> Available: {fmt(availableCredit)}, Order total: {fmt(orderTotal)}
          </div>
        </div>
      )}
    </div>
  );

  const CardSection = () => (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <CreditCard size={18} className="text-secondary" />
        <span className="fw-semibold">Card Details</span>
      </div>
      <div className="form-control" style={{ height: 'auto', padding: '10px 12px', borderRadius: '0.375rem' }}>
        <CardElement options={CARD_STYLE} />
      </div>
    </div>
  );

  // ── Selected pickup location details ─────────────────────────────────────────
  const selectedLocation = pickupLocations.find(l => String(l.id) === String(pickupLocationId)) || null;

  // ── Main render ──────────────────────────────────────────────────────────────
  return (
    <div className="container py-4">
      <h2 className="mb-4 fw-bold">Checkout</h2>

      {error && (
        <div className="alert alert-danger d-flex align-items-start gap-2" role="alert">
          <AlertCircle size={18} className="flex-shrink-0 mt-1" />
          <div>{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row g-4">

          {/* ── Left column ──────────────────────────────────────────────── */}
          <div className="col-md-8">

            {/* Order Items */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white border-bottom py-3">
                <h5 className="mb-0 fw-semibold">Order Items</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">Product</th>
                        <th className="text-center">Qty</th>
                        <th className="text-end">Unit Price</th>
                        <th className="text-end pe-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={item.productID || idx}>
                          <td className="ps-3">
                            {productNames[item.productID] || `Product #${item.productID}`}
                            {item.priceSource && (
                              <span className="badge bg-light text-muted ms-2 fw-normal small">{item.priceSource}</span>
                            )}
                          </td>
                          <td className="text-center">{item.quantity}</td>
                          <td className="text-end">{fmt(item.price)}</td>
                          <td className="text-end pe-3">{fmt(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan={3} className="text-end fw-semibold ps-3">Subtotal</td>
                        <td className="text-end fw-bold pe-3">{fmt(subtotal)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            {/* Fulfillment */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white border-bottom py-3">
                <h5 className="mb-0 fw-semibold">Fulfillment</h5>
              </div>
              <div className="card-body">

                {/* Type selector — Ship / Pickup only */}
                <div className="btn-group w-100 mb-4" role="group" aria-label="Fulfillment type">
                  <input type="radio" className="btn-check" name="fulfillmentType" id="ftShip" value="ship"
                    autoComplete="off" checked={fulfillmentType === 'ship'}
                    onChange={() => setFulfillmentType('ship')} />
                  <label className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2" htmlFor="ftShip">
                    <Truck size={16} /> Ship
                  </label>
                  <input type="radio" className="btn-check" name="fulfillmentType" id="ftPickup" value="pickup"
                    autoComplete="off" checked={fulfillmentType === 'pickup'}
                    onChange={() => setFulfillmentType('pickup')} />
                  <label className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2" htmlFor="ftPickup">
                    <MapPin size={16} /> Pickup
                  </label>
                </div>

                {/* Ship: address + shipping cost */}
                {fulfillmentType === 'ship' && (
                  <div>
                    {shippingAddress ? (
                      <address className="mb-3">
                        <div className="text-muted small fw-semibold mb-1">Ship to</div>
                        <div>{shippingAddress.addressLine}</div>
                        <div>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}</div>
                      </address>
                    ) : (
                      <div className="d-flex align-items-center gap-2 text-warning mb-3">
                        <AlertCircle size={18} />
                        <span>
                          No shipping address on file —{' '}
                          <Link to="/wholesale/profile" className="text-warning fw-semibold">edit your profile</Link>{' '}
                          to add one.
                        </span>
                      </div>
                    )}

                    {/* Shipping cost + free shipping nudge */}
                    <div className="border rounded p-3 bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">Shipping</span>
                        <span className="fw-semibold">
                          {shippingCost === 0
                            ? <span className="text-success">Free</span>
                            : fmt(shippingCost)
                          }
                        </span>
                      </div>
                      {shippingInfo.shipping_rule_description && (
                        <div className="text-muted small mt-1">{shippingInfo.shipping_rule_description}</div>
                      )}
                      {shippingInfo.cases_until_free !== null && shippingInfo.cases_until_free > 0 && (
                        <div className="alert alert-info py-2 px-3 mb-0 mt-2 small d-flex align-items-center gap-2">
                          <Truck size={14} className="flex-shrink-0" />
                          Add <strong className="mx-1">{shippingInfo.cases_until_free} more case{shippingInfo.cases_until_free !== 1 ? 's' : ''}</strong> for free shipping.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Pickup: radio list of locations */}
                {fulfillmentType === 'pickup' && (
                  <div>
                    {pickupLocations.length === 0 ? (
                      <p className="text-muted small mb-0">No pickup locations available at this time.</p>
                    ) : (
                      <div className="d-flex flex-column gap-3">
                        {pickupLocations.map(loc => (
                          <label
                            key={loc.id}
                            className={`d-flex gap-3 p-3 border rounded cursor-pointer ${String(pickupLocationId) === String(loc.id) ? 'border-primary bg-light' : ''}`}
                            style={{ cursor: 'pointer' }}
                          >
                            <input
                              type="radio"
                              name="pickupLocation"
                              value={loc.id}
                              checked={String(pickupLocationId) === String(loc.id)}
                              onChange={() => setPickupLocationId(String(loc.id))}
                              className="form-check-input flex-shrink-0 mt-1"
                            />
                            <div className="flex-grow-1">
                              <div className="fw-semibold">{loc.name}</div>
                              <div className="text-muted small">
                                {loc.address_line1}
                                {loc.address_line2 && `, ${loc.address_line2}`}
                                {', '}{loc.city}, {loc.state} {loc.zip}
                              </div>
                              <div className="small mt-1">
                                <span className="badge bg-secondary fw-normal">Ready in {loc.lead_time_days} business day{loc.lead_time_days !== 1 ? 's' : ''}</span>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    {selectedLocation?.pickup_instructions && (
                      <div className="alert alert-light border mt-3 small mb-0">
                        <strong>Pickup instructions:</strong> {selectedLocation.pickup_instructions}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white border-bottom py-3">
                <h5 className="mb-0 fw-semibold">Payment</h5>
              </div>
              <div className="card-body">
                {paymentType === 'both' && (
                  <div className="mb-4">
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="paymentMethod" id="methodInvoice"
                          value="invoice" checked={chosenMethod === 'invoice'} onChange={() => setChosenMethod('invoice')} />
                        <label className="form-check-label" htmlFor="methodInvoice">
                          <FileText size={15} className="me-1 text-secondary" /> Pay by Invoice
                        </label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="paymentMethod" id="methodCard"
                          value="credit_card" checked={chosenMethod === 'credit_card'} onChange={() => setChosenMethod('credit_card')} />
                        <label className="form-check-label" htmlFor="methodCard">
                          <CreditCard size={15} className="me-1 text-secondary" /> Pay by Card
                        </label>
                      </div>
                    </div>
                    <hr className="mt-3 mb-0" />
                  </div>
                )}
                {(paymentType === 'invoice' || (paymentType === 'both' && chosenMethod === 'invoice')) && <InvoiceSection />}
                {(paymentType === 'credit_card' || (paymentType === 'both' && chosenMethod === 'credit_card')) && <CardSection />}
              </div>
            </div>

            {/* Minimum order guard */}
            {belowMinimum && (
              <div className="alert alert-warning d-flex align-items-start gap-2 mb-4" role="alert">
                <AlertCircle size={18} className="flex-shrink-0 mt-1" />
                <div>
                  A minimum order of 1 case (12 bottles) is required. Your cart contains{' '}
                  <strong>{totalBottles} bottle{totalBottles !== 1 ? 's' : ''}</strong>.
                </div>
              </div>
            )}

            {/* Submit */}
            <div className="d-grid">
              <button type="submit" className="btn btn-primary btn-lg" disabled={isSubmitDisabled}>
                {submitting ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Processing…</>
                ) : effectiveMethod === 'credit_card' ? (
                  <><CreditCard size={18} className="me-2" />Pay Now {fmt(orderTotal)}</>
                ) : (
                  <><CheckCircle size={18} className="me-2" />Place Order</>
                )}
              </button>
            </div>
          </div>

          {/* ── Right column: Order Summary ──────────────────────────────── */}
          <div className="col-md-4">
            <div className="card border-0 shadow-sm sticky-top" style={{ top: '1.5rem' }}>
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                  <ShoppingCart size={16} className="text-secondary" />
                  Order Summary
                </h6>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2 text-muted small">
                  <span>Items ({itemCount})</span>
                  <span>{fmt(subtotal)}</span>
                </div>

                {fulfillmentType === 'ship' && (
                  <div className="d-flex justify-content-between mb-2 text-muted small">
                    <span>
                      Shipping
                      {shippingInfo.shipping_rule_description && (
                        <span className="ms-1 text-muted" style={{ fontSize: '0.75rem' }}>
                          ({shippingInfo.shipping_rule_description})
                        </span>
                      )}
                    </span>
                    <span className={shippingCost === 0 ? 'text-success' : ''}>
                      {shippingCost === 0 ? 'Free' : fmt(shippingCost)}
                    </span>
                  </div>
                )}

                <hr />
                <div className="d-flex justify-content-between fw-bold mb-3">
                  <span>Total</span>
                  <span>{fmt(orderTotal)}</span>
                </div>

                <div className="small text-muted">
                  {fulfillmentType === 'ship' && (
                    <>
                      <div className="fw-semibold mb-1">Ship to</div>
                      {shippingAddress ? (
                        <address className="mb-0 small">
                          <div>{shippingAddress.addressLine}</div>
                          <div>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}</div>
                        </address>
                      ) : (
                        <span className="text-warning small">
                          No address on file. <Link to="/wholesale/profile" className="text-warning">Add one</Link>
                        </span>
                      )}
                    </>
                  )}
                  {fulfillmentType === 'pickup' && selectedLocation && (
                    <>
                      <div className="fw-semibold mb-1">Pickup at</div>
                      <div>{selectedLocation.name}</div>
                      <div>{selectedLocation.address_line1}{selectedLocation.address_line2 ? `, ${selectedLocation.address_line2}` : ''}</div>
                      <div>{selectedLocation.city}, {selectedLocation.state} {selectedLocation.zip}</div>
                      <div className="mt-1 text-muted">
                        Ready in ~{selectedLocation.lead_time_days} business day{selectedLocation.lead_time_days !== 1 ? 's' : ''}
                      </div>
                    </>
                  )}
                </div>

                {paymentType === 'invoice' && netDays != null && (
                  <div className="mt-3 pt-3 border-top small text-muted">
                    <FileText size={13} className="me-1" />Net {netDays} days invoice
                  </div>
                )}
              </div>
              <div className="card-footer bg-white border-top pt-3 pb-3">
                <Link to="/wholesale/catalog" className="text-decoration-none small text-muted">
                  &larr; Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function Checkout() {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm />
    </Elements>
  );
}
